import { useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { toast } from 'sonner'
import { indexarConteudo } from '../lib/indexar'

const BUCKET = 'notas'
const SIGNED_URL_TTL = 14400 // 4 horas

export const SELECT_NOTA = `
  id, tipo, conteudo, legenda, sessao_uuid, projeto_id,
  storage_path, legenda_audio_path, tamanho_bytes,
  duracao_segundos, latitude, longitude, criado_em
`

export async function getGeo() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
    )
  })
}

export async function attachSignedUrls(items) {
  const withFiles = items.filter(n => n.storage_path)
  if (!withFiles.length) return items
  const paths = withFiles.map(n => n.storage_path)
  const { data: signed } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_URL_TTL)
  const urlMap = {}
  ;(signed ?? []).forEach(({ path, signedUrl }) => {
    const nota = withFiles.find(n => n.storage_path === path)
    if (nota && signedUrl) urlMap[nota.id] = signedUrl
  })
  return items.map(n => ({ ...n, signedUrl: urlMap[n.id] ?? null }))
}

/**
 * Hook interno para operacoes de nota (CRUD + upload).
 * Recebe estado compartilhado do useSessaoCaptura.
 */
export function useSessaoCapturaNotas({ sessaoAtiva, setItems, itemsRef }) {

  // ── Upload de arquivo para Storage ──
  const _uploadFile = useCallback(async (file, sessaoUuid, tipo) => {
    const ext  = file.name ? file.name.split('.').pop() : (tipo === 'AUDIO' ? 'webm' : 'jpg')
    const path = `uploads/${sessaoUuid}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 30000)
    try {
      const { error: uploadError } = await supabase.storage
        .from(BUCKET).upload(path, file, {
          contentType: file.type ?? 'application/octet-stream',
          ...(typeof AbortController !== 'undefined' ? { signal: controller.signal } : {}),
        })
      clearTimeout(timer)
      if (uploadError) return { path: null, error: 'Erro no upload' }
    } catch (e) {
      clearTimeout(timer)
      return { path: null, error: e.name === 'AbortError' ? 'Upload timeout — verifique sua conexão' : 'Erro no upload' }
    }
    return { path, error: null }
  }, [])

  // ── Adicionar nota a sessao ativa ──
  const adicionarNota = useCallback(async ({ tipo, conteudo = null, file = null, legenda = null }) => {
    if (!sessaoAtiva) return null
    const geo = await getGeo()

    let storage_path   = null
    let tamanho_bytes  = null

    if (file && ['FOTO', 'DESENHO', 'AUDIO'].includes(tipo)) {
      const { path, error: upErr } = await _uploadFile(file, sessaoAtiva.uuid, tipo)
      if (upErr) { toast.error(upErr); return null }
      storage_path  = path
      tamanho_bytes = file.size ?? null
    }

    const { data, error } = await supabase
      .from('notas')
      .insert({
        tipo, conteudo, legenda,
        sessao_uuid:  sessaoAtiva.uuid,
        projeto_id:   sessaoAtiva.projeto_id,
        storage_path, tamanho_bytes,
        ...(geo ?? {}),
      })
      .select(SELECT_NOTA)
      .single()
    if (error) { toast.error('Erro ao salvar nota'); return null }

    let notaComUrl = data
    if (data.storage_path) {
      const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(data.storage_path, SIGNED_URL_TTL)
      if (urlData?.signedUrl) notaComUrl = { ...data, signedUrl: urlData.signedUrl }
    }
    setItems(prev => [...prev, notaComUrl])
    // Fire-and-forget: indexa conteudo para busca semantica
    if (sessaoAtiva?.projeto_id) {
      const base = { projeto_id: sessaoAtiva.projeto_id, fonte: 'notas', fonte_id: data.id }
      if (tipo === 'TEXTO' && conteudo) {
        indexarConteudo({ ...base, texto: conteudo, prefixo: `[Nota de campo${legenda ? ' — ' + legenda : ''}]` })
      } else if (storage_path && ['FOTO', 'DESENHO'].includes(tipo)) {
        indexarConteudo({ ...base, bucket: BUCKET, storage_path, prefixo: `[${tipo === 'FOTO' ? 'Foto' : 'Desenho'} de campo${legenda ? ' — ' + legenda : ''}]` })
      }
    }
    return notaComUrl
  }, [sessaoAtiva, _uploadFile, setItems])

  // ── Adicionar nota com upload em background (camera rapida) ──
  const adicionarNotaBackground = useCallback(async ({ tipo, file }) => {
    if (!sessaoAtiva || !file) return null

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const localUrl = URL.createObjectURL(file)

    const placeholder = {
      id: tempId, tipo, conteudo: null, legenda: null,
      sessao_uuid: sessaoAtiva.uuid, projeto_id: sessaoAtiva.projeto_id,
      storage_path: null, tamanho_bytes: file.size,
      criado_em: new Date().toISOString(),
      signedUrl: localUrl, _uploading: true,
    }
    setItems(prev => [...prev, placeholder])

    try {
      const [{ path, error: upErr }, geo] = await Promise.all([
        _uploadFile(file, sessaoAtiva.uuid, tipo),
        getGeo(),
      ])

      if (upErr) {
        setItems(prev => prev.map(n => n.id === tempId ? { ...n, _uploading: false, _error: upErr } : n))
        toast.error(upErr)
        return null
      }

      const { data, error } = await supabase
        .from('notas')
        .insert({
          tipo, sessao_uuid: sessaoAtiva.uuid, projeto_id: sessaoAtiva.projeto_id,
          storage_path: path, tamanho_bytes: file.size ?? null, ...(geo ?? {}),
        })
        .select(SELECT_NOTA)
        .single()

      if (error) {
        setItems(prev => prev.map(n => n.id === tempId ? { ...n, _uploading: false, _error: 'Erro ao salvar' } : n))
        toast.error('Erro ao salvar nota')
        return null
      }

      let signedUrl = localUrl
      if (data.storage_path) {
        const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(data.storage_path, SIGNED_URL_TTL)
        if (urlData?.signedUrl) signedUrl = urlData.signedUrl
      }

      const realNota = { ...data, signedUrl, _uploading: false }
      setItems(prev => prev.map(n => n.id === tempId ? realNota : n))
      URL.revokeObjectURL(localUrl)

      if (sessaoAtiva?.projeto_id && path) {
        indexarConteudo({
          projeto_id: sessaoAtiva.projeto_id, fonte: 'notas', fonte_id: data.id,
          bucket: BUCKET, storage_path: path, prefixo: `[Foto de campo]`,
        })
      }
      return realNota
    } catch (err) {
      setItems(prev => prev.map(n => n.id === tempId ? { ...n, _uploading: false, _error: 'Erro inesperado' } : n))
      return null
    }
  }, [sessaoAtiva, _uploadFile, setItems])

  // ── Deletar nota ──
  const deletarNota = useCallback(async (nota) => {
    if (nota.storage_path) {
      await supabase.storage.from(BUCKET).remove([nota.storage_path])
    }
    await supabase.from('notas').delete().eq('id', nota.id)
    setItems(prev => prev.filter(n => n.id !== nota.id))
    if (nota.projeto_id) {
      indexarConteudo({ projeto_id: nota.projeto_id, fonte: 'notas', fonte_id: nota.id, modo: 'apagar' })
    }
  }, [setItems])

  // ── Adicionar legenda de voz a nota existente ──
  const adicionarLegendaVoz = useCallback(async (nota_id, audioBlob) => {
    const nota = itemsRef.current.find(n => n.id === nota_id)
    if (!nota) return null
    const path = `uploads/legenda_${nota_id}_${Date.now()}.webm`
    const { error: uploadError } = await supabase.storage
      .from(BUCKET).upload(path, audioBlob, { contentType: 'audio/webm' })
    if (uploadError) { toast.error('Erro ao salvar legenda de voz'); return null }
    const { error } = await supabase.from('notas').update({ legenda_audio_path: path }).eq('id', nota_id)
    if (error) { toast.error('Erro ao atualizar nota'); return null }
    const updated = { ...nota, legenda_audio_path: path }
    setItems(prev => prev.map(n => n.id === nota_id ? updated : n))
    return updated
  }, [setItems, itemsRef])

  // ── Atualizar campos de uma nota (legenda, conteudo) ──
  const atualizarNota = useCallback(async (notaId, campos) => {
    const { error } = await supabase.from('notas').update(campos).eq('id', notaId)
    if (error) { toast.error('Erro ao atualizar nota'); return false }
    setItems(prev => prev.map(n => n.id === notaId ? { ...n, ...campos } : n))
    return true
  }, [setItems])

  return {
    adicionarNota,
    adicionarNotaBackground,
    deletarNota,
    adicionarLegendaVoz,
    atualizarNota,
  }
}
